<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted } from 'vue';
import Lazy from '@/components/Lazy.vue';
import { useHead } from '@vueuse/head';
import { useRoute } from 'vue-router';
import { VUE_APP_BASE_URL } from '@/env';
import MainNavShell from '@/components/MainNavShell.vue';
import { onCLS, onINP, onLCP, type CLSMetricWithAttribution, type INPMetricWithAttribution, type LCPMetricWithAttribution } from 'web-vitals/attribution';
import { log } from '@/otel';
import { SeverityNumber } from '@opentelemetry/api-logs';

const WhatsApp = defineAsyncComponent({
    loader: () => import(/** webpackChunkName: "WhatsApp" */ '@/components/WhatsApp.vue')
});

const CookiesBar = defineAsyncComponent({
    loader: () => import(/** webpackChunkName: "CookiesBar" */ '@/components/CookiesBar.vue')
});

const MainNav = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "MainNav" */ '@/components/MainNav.vue')
});

const route = useRoute();

const canonicalUrl = computed(() => {
    return VUE_APP_BASE_URL + route.path;
});


useHead({
    link: [
        {
            rel: 'canonical',
            href: canonicalUrl
        }
    ]
});

onMounted(() => {
    onCLS(sendToAnalytics);
    onINP(sendToAnalytics);
    onLCP(sendToAnalytics);
});

function sendToAnalytics(metric: CLSMetricWithAttribution | INPMetricWithAttribution | LCPMetricWithAttribution) {
    const poorThresholds = {
        CLS: 0.25,
        INP: 500,
        LCP: 4000,
    };

    const needsImprovementThresholds = {
        CLS: 0.1,
        INP: 200,
        LCP: 2500,
    };

    let severityNumber: SeverityNumber;
    let severityText: string;

    if (metric.value > poorThresholds[metric.name]) {
        severityNumber = SeverityNumber.WARN; // WARN
        severityText = 'POOR';
    } else if (metric.value > needsImprovementThresholds[metric.name]) {
        severityNumber = SeverityNumber.INFO; // INFO
        severityText = 'NEEDS IMPROVEMENT';
    } else {
        return; // It's a "Good" score, don't log it.
    }

    const logMessage = `Bad Web Vital Detected: ${metric.name}`;

    // Prepare attributes
    const attributes: Record<string, string | number | undefined> = {
        'web_vital.name': metric.name,
        'web_vital.value': metric.value,
        'web_vital.rating': severityText,
        'web_vital.id': metric.id, // A unique ID for this page load
        'page.path': window.location.pathname,
        'browser.user_agent': navigator.userAgent,
        'browser.viewport.width': window.innerWidth,
        'browser.viewport.height': window.innerHeight,
    };

    // Add attribution data specific to the metric
    if (metric.name === 'CLS') {
        attributes['web_vital.cls.largest_shift_target'] = metric.attribution.largestShiftTarget;
        attributes['web_vital.cls.load_state'] = metric.attribution.loadState;
    } else if (metric.name === 'LCP') {
        attributes['web_vital.lcp.target'] = metric.attribution.target;
        attributes['web_vital.lcp.url'] = metric.attribution.url;
    } else if (metric.name === 'INP') {
        attributes['web_vital.inp.interaction_target'] = metric.attribution.interactionTarget;
        attributes['web_vital.inp.interaction_type'] = metric.attribution.interactionType;
    }

    log(severityNumber, logMessage, attributes);
}

</script>
<template>
    <Suspense>
        <Lazy>
            <MainNav />
        </Lazy>
        <template #fallback>
            <MainNavShell />
        </template>
    </Suspense>

    <div class="router-wrapper">
        <RouterView v-slot="{ Component }">
            <Transition name="fade" mode="default">
                <component :is="Component" />
            </Transition>
        </RouterView>
    </div>

    <!-- COOKIES BAR -->
    <Suspense>
        <Lazy>
            <CookiesBar />
        </Lazy>
    </Suspense>
    <!-- WHATSAPP -->
    <Suspense>
        <Lazy>
            <WhatsApp />
        </Lazy>
    </Suspense>
</template>
<style lang="scss">
@import '@/assets/sass/main.scss';

.router-wrapper {
    position: relative;
    width: 100%;
}

.fade-enter-active,
.fade-leave-active {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    transition: opacity 0.2s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
